import { FormEvent, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCommunicationRealtime,
  useConversationRead,
  post,
  useConversationMessages,
  useConversationComposer,
  useSession,
} from '@yingbaba/api';
import {
  Avatar,
  Button,
  CommunicationAttachment,
  CommunicationConnectionNotice,
  ErrorNotice,
  useConversationScroll,
} from '@yingbaba/ui';
import { Upload, MessageSquare, Send, X } from '@yingbaba/icons';
import { datetime } from '@yingbaba/utils';
import type { Conversation } from '@yingbaba/types';

export function openSupport() {
  window.dispatchEvent(new Event('yingbaba:open-support'));
}

export function SupportWidget() {
  const session = useSession();
  const location = useLocation();
  const accountID = session.data?.account.id;
  const qc = useQueryClient();
  const [open, setOpen] = useState(new URLSearchParams(location.search).get('support') === '1');
  const [contact, setContact] = useState<{ accountID: string; conversation: Conversation } | null>(
    null,
  );
  const conversation = contact?.accountID === accountID ? contact?.conversation || null : null;
  const {
    text,
    setText,
    sending,
    uploading,
    error: composerError,
    sendText,
    attach,
  } = useConversationComposer({
    conversationID: conversation?.id || '',
    accountID: accountID || '',
    basePath: '/customer-service',
    fileDeletePath: '/customer-service/files',
    listPath: '/customer-service/conversation',
  });
  const busy = sending || uploading;
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const messageFeed = useConversationMessages(
    conversation
      ? `/customer-service/conversations/${conversation.id}/messages`
      : '/customer-service/conversations/none/messages',
    open && !!conversation,
    80,
  );
  const messages = messageFeed.latest;

  useEffect(() => {
    const openPanel = () => setOpen(true);
    window.addEventListener('yingbaba:open-support', openPanel);
    return () => window.removeEventListener('yingbaba:open-support', openPanel);
  }, []);

  useEffect(() => {
    setContact(null);
    setError(null);
  }, [accountID]);

  useEffect(() => {
    if (!open || !accountID || conversation) return;
    let disposed = false;
    setError(null);
    void post<Conversation>('/customer-service/conversation')
      .then((value) => {
        if (!disposed) setContact({ accountID, conversation: value });
      })
      .catch((reason) => {
        if (!disposed) setError(reason);
      });
    return () => {
      disposed = true;
    };
  }, [open, accountID, conversation, connectionAttempt]);

  const returnParams = new URLSearchParams(location.search);
  returnParams.set('support', '1');
  const supportReturnTo = location.pathname + '?' + returnParams.toString() + location.hash;

  const connection = useCommunicationRealtime({
    accountID: accountID || '',
    enabled: open && !!conversation,
    onEvent: (envelope) => {
      if (
        envelope.type !== 'connected' &&
        !(
          envelope.type === 'message.created' &&
          envelope.payload?.conversation_id === conversation?.id
        )
      )
        return;
      void qc.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].startsWith(
            `/customer-service/conversations/${conversation?.id}/messages`,
          ),
      });
    },
  });
  useConversationRead({
    accountID: accountID || '',
    path: `/customer-service/conversations/${conversation?.id}/read`,
    seq: messageFeed.items.at(-1)?.seq,
    enabled: open && !!conversation,
  });

  const { viewportRef, onScroll, loadEarlier } = useConversationScroll(
    `${accountID || ''}:${conversation?.id || ''}:${open}`,
    messageFeed.items,
    accountID || '',
    messageFeed.loadOlder,
  );

  function send(event: FormEvent) {
    event.preventDefault();
    void sendText();
  }

  return (
    <div className={`support-widget ${open ? 'open' : ''}`}>
      {open ? (
        <section className="support-panel" aria-label="在线咨询">
          <header className="support-header">
            <div>
              <strong>影爸爸在线客服</strong>
              <span>围绕需求、项目和平台使用提供帮助</span>
            </div>
            <button
              type="button"
              className="icon-btn"
              aria-label="关闭在线咨询"
              onClick={() => setOpen(false)}
            >
              <X size={18} />
            </button>
          </header>
          {!session.data ? (
            <div className="support-login">
              <MessageSquare size={28} />
              <strong>登录后开始咨询</strong>
              <p>登录后消息会保存在你的账号下，切换设备也能继续。</p>
              <Link
                className="btn btn-primary"
                to={'/login?returnTo=' + encodeURIComponent(supportReturnTo)}
              >
                登录 / 注册
              </Link>
            </div>
          ) : (
            <>
              <ErrorNotice error={error || composerError || messageFeed.error || messages.error} />
              <CommunicationConnectionNotice state={conversation ? connection : undefined} />
              {!conversation && error ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConnectionAttempt((attempt) => attempt + 1)}
                >
                  重试连接客服
                </Button>
              ) : null}
              <div
                className="support-messages"
                ref={viewportRef}
                onScroll={onScroll}
                aria-live="polite"
              >
                {!conversation && !error ? <p className="small muted">正在连接客服…</p> : null}
                {messageFeed.hasMore ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={messageFeed.loadingOlder}
                    onClick={() => void loadEarlier()}
                  >
                    加载更早消息
                  </Button>
                ) : null}
                {messageFeed.items.map((message) => {
                  const mine = message.sender_id === session.data?.account.id;
                  return (
                    <article
                      className={`communication-message ${mine ? 'mine' : ''}`}
                      key={message.id}
                    >
                      <div className="communication-message-meta">
                        <Avatar
                          name={message.sender_name || '成员'}
                          src={message.sender_avatar_url}
                          size={28}
                        />
                        <strong>{mine ? '我' : message.sender_name || '客服'}</strong>
                        <time>{datetime(message.created_at)}</time>
                      </div>
                      {message.content &&
                      !message.attachments.some((file) => file.file_name === message.content) ? (
                        <p>{message.content}</p>
                      ) : null}
                      {message.attachments.map((file) => (
                        <CommunicationAttachment
                          key={file.id}
                          file={file}
                          previewPath={`/customer-service/files/${file.id}/preview`}
                          thumbnailPath={`/customer-service/files/${file.id}/thumbnail`}
                          downloadPath={`/customer-service/files/${file.id}/download`}
                        />
                      ))}
                    </article>
                  );
                })}
                {!messageFeed.items.length ? (
                  <div className="communication-empty">你好，有什么可以帮你？</div>
                ) : null}
              </div>
              <form className="support-composer" onSubmit={send}>
                <label className="icon-btn support-file" aria-label="发送附件">
                  <Upload size={17} />
                  <input
                    type="file"
                    disabled={busy || !conversation}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      void attach(file);
                      event.currentTarget.value = '';
                    }}
                  />
                </label>
                <input
                  className="input grow"
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  aria-label="咨询消息"
                  placeholder={conversation ? '输入消息…' : '正在连接客服…'}
                  disabled={!conversation}
                  maxLength={8000}
                />
                <Button
                  aria-label="发送消息"
                  size="sm"
                  loading={busy}
                  disabled={!conversation || !text.trim()}
                >
                  <Send size={15} />
                </Button>
              </form>
            </>
          )}
        </section>
      ) : (
        <button
          type="button"
          className="support-fab"
          aria-label="在线咨询"
          title="在线咨询"
          onClick={() => setOpen(true)}
        >
          <MessageSquare size={19} />
          <span>在线咨询</span>
        </button>
      )}
    </div>
  );
}
